import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref } from 'vue'
import { ErrorCodes, type BaseError, MetaErrorCodes } from '@cplug/frontend-core/models'
import { Result } from '@cplug/frontend-core/utils'
import CPTwoFactorGateway from '../gateways/CPTwoFactorGateway'
import type { TwoFactorBackupCodes } from '../models/TwoFactorBackupCodes'
import { TwoFactorMethod } from '../models/TwoFactorMethod'
import { TwoFactorSetupStep } from '../models/TwoFactorSetupStep'
import type { TwoFactorStatus } from '../models/TwoFactorStatus'
import { TwoFactorEmailOtpContext } from '../models/TwoFactorEmailOtpContext'
import type { TwoFactorTotpSetup } from '../models/TwoFactorTotpSetup'

const useTwoFactorStore = defineStore('shared/two-factor', () => {
  const gateway = CPTwoFactorGateway.instance()
  const model = ref<TwoFactorStatus>({
    enabled: false,
    method: undefined
  })
  const errors = ref<BaseError[]>([])

  const loading = ref(false)
  const setupStep = ref<TwoFactorSetupStep | undefined>()
  const setupChallengeId = ref<string | undefined>()
  const selectedMethod = ref<TwoFactorMethod | undefined>()
  const totpSetup = ref<TwoFactorTotpSetup | undefined>()
  const backupCodes = ref<TwoFactorBackupCodes | undefined>()
  const backupCodesRemaining = ref<number | undefined>()
  const setupEmailResendBlocked = ref(false)
  const verificationBlocked = ref(false)

  function defaultStatus (): TwoFactorStatus {
    return {
      enabled: false,
      method: undefined
    }
  }

  function validationError (field: string, code: MetaErrorCodes = MetaErrorCodes.WrongFormat): BaseError {
    return { code: ErrorCodes.DataValidation, meta: { [field]: code } }
  }

  function setVerificationError (error: BaseError): void {
    errors.value = [error]

    if (error.code === ErrorCodes.RateLimit) {
      verificationBlocked.value = true
    }
  }

  async function withLoading<T> (action: () => Promise<Result<T>>): Promise<Result<T>> {
    loading.value = true
    errors.value = []

    const result = await action()

    result.ifError((error) => {
      errors.value = [error]
    })

    loading.value = false

    return result
  }

  function sendBackupCodesEmail (context: TwoFactorEmailOtpContext): Promise<Result<undefined>> {
    return withLoading(() =>
      context === TwoFactorEmailOtpContext.Reveal
        ? gateway.sendRevealBackupCodesEmail()
        : gateway.sendRegenerateBackupCodesEmail()
    )
  }

  function resendBackupCodesEmail (context: TwoFactorEmailOtpContext): Promise<Result<undefined>> {
    return withLoading(() =>
      context === TwoFactorEmailOtpContext.Reveal
        ? gateway.resendRevealBackupCodesEmail()
        : gateway.resendRegenerateBackupCodesEmail()
    )
  }

  async function fetchStatus (): Promise<Result<TwoFactorStatus>> {
    loading.value = true
    errors.value = []

    const result = await gateway.getStatus()

    result
      .ifSuccess((status) => {
        model.value = status
      })
      .ifError((error) => {
        errors.value = [error]
      })

    loading.value = false

    return result
  }

  async function fetchBackupCodesRemaining (): Promise<Result<number>> {
    const result = await gateway.getBackupCodesRemaining()

    result
      .ifSuccess((data) => {
        backupCodesRemaining.value = data.remaining
      })
      .ifError((error) => {
        errors.value = [error]
      })

    return result.map(data => data.remaining)
  }

  async function startTotpSetup (): Promise<Result<TwoFactorTotpSetup>> {
    loading.value = true
    errors.value = []
    verificationBlocked.value = false
    selectedMethod.value = TwoFactorMethod.Totp
    setupStep.value = TwoFactorSetupStep.TotpSetup

    const result = await gateway.setupTotp()

    result
      .ifSuccess((setup) => {
        totpSetup.value = setup
        setupChallengeId.value = setup.setupChallengeId
      })
      .ifError((error) => {
        errors.value = [error]
      })

    loading.value = false

    return result
  }

  async function startEmailSetup (): Promise<Result<string>> {
    loading.value = true
    errors.value = []
    verificationBlocked.value = false
    setupEmailResendBlocked.value = false
    selectedMethod.value = TwoFactorMethod.Email
    setupStep.value = TwoFactorSetupStep.EmailSetup
    totpSetup.value = undefined

    const result = await gateway.setupEmail()

    result
      .ifSuccess((challenge) => {
        setupChallengeId.value = challenge.setupChallengeId
      })
      .ifError((error) => {
        errors.value = [error]
      })

    loading.value = false

    return result.map(challenge => challenge.setupChallengeId)
  }

  async function resendSetupEmail (): Promise<Result<undefined>> {
    if (!setupChallengeId.value) {
      return Result.ofError({ code: ErrorCodes.Unknown })
    }

    loading.value = true
    errors.value = []

    const result = await gateway.resendSetupEmail(setupChallengeId.value)

    result.ifError((error) => {
      errors.value = [error]

      if (error.code === ErrorCodes.RateLimit) {
        setupEmailResendBlocked.value = true
      }
    })

    loading.value = false

    return result
  }

  async function verifySetup (code: string): Promise<Result<undefined>> {
    if (!setupChallengeId.value) {
      return Result.ofError({ code: ErrorCodes.Unknown })
    }

    if (code.trim().length !== 6) {
      errors.value = [validationError('code')]
      return Result.ofError({ code: ErrorCodes.DataValidation, meta: { code: MetaErrorCodes.WrongFormat } })
    }

    loading.value = true
    errors.value = []

    const result = await gateway.verifySetup(setupChallengeId.value, code)

    result.ifError((error) => {
      setVerificationError(error)
    })

    loading.value = false

    return result
  }

  async function confirmSetup (revokeOtherSessions = false): Promise<Result<TwoFactorBackupCodes>> {
    if (!setupChallengeId.value) {
      return Result.ofError({ code: ErrorCodes.Unknown })
    }

    loading.value = true
    errors.value = []

    const result = await gateway.confirmSetup(
      setupChallengeId.value,
      revokeOtherSessions
    )

    result
      .ifSuccess((codes) => {
        backupCodes.value = codes
        setupStep.value = TwoFactorSetupStep.BackupCodes
      })
      .ifError((error) => {
        errors.value = [error]
      })

    loading.value = false

    return result
  }

  async function disable (password: string, revokeOtherSessions = false): Promise<Result<undefined>> {
    if ((password.trim().length ?? 0) < 6) {
      errors.value = [validationError('password', MetaErrorCodes.Empty)]
      return Result.ofError({ code: ErrorCodes.DataValidation, meta: { password: MetaErrorCodes.Empty } })
    }

    loading.value = true
    errors.value = []

    const result = await gateway.disable(password, revokeOtherSessions)

    result
      .ifSuccess(async () => {
        model.value = defaultStatus()
        backupCodesRemaining.value = undefined
        backupCodes.value = undefined
        await fetchStatus()
      })
      .ifError((error) => {
        errors.value = [error]
      })

    loading.value = false

    return result
  }

  async function regenerateBackupCodes (code?: string): Promise<Result<TwoFactorBackupCodes>> {
    const normalizedCode = code?.trim()

    if (normalizedCode != null && normalizedCode !== '' && (normalizedCode.length < 6 || normalizedCode.length > 12)) {
      errors.value = [validationError('code')]
      return Result.ofError({ code: ErrorCodes.DataValidation, meta: { code: MetaErrorCodes.WrongFormat } })
    }

    loading.value = true
    errors.value = []

    const result = await gateway.regenerateBackupCodes(normalizedCode)

    result
      .ifSuccess((codes) => {
        backupCodes.value = codes
        setupStep.value = TwoFactorSetupStep.BackupCodes
      })
      .ifError((error) => {
        setVerificationError(error)
      })

    loading.value = false

    return result
  }

  async function revealBackupCodes (code: string): Promise<Result<TwoFactorBackupCodes>> {
    const normalizedCode = code.trim()

    if (normalizedCode.length < 6 || normalizedCode.length > 12) {
      errors.value = [validationError('code')]
      return Result.ofError({ code: ErrorCodes.DataValidation, meta: { code: MetaErrorCodes.WrongFormat } })
    }

    loading.value = true
    errors.value = []

    const result = await gateway.revealBackupCodes(normalizedCode)

    result
      .ifSuccess((codes) => {
        backupCodes.value = codes
      })
      .ifError((error) => {
        setVerificationError(error)
      })

    loading.value = false

    return result
  }

  function sendRevealBackupCodesEmail (): Promise<Result<undefined>> {
    return sendBackupCodesEmail(TwoFactorEmailOtpContext.Reveal)
  }

  function resendRevealBackupCodesEmail (): Promise<Result<undefined>> {
    return resendBackupCodesEmail(TwoFactorEmailOtpContext.Reveal)
  }

  function sendRegenerateBackupCodesEmail (): Promise<Result<undefined>> {
    return sendBackupCodesEmail(TwoFactorEmailOtpContext.Regenerate)
  }

  function resendRegenerateBackupCodesEmail (): Promise<Result<undefined>> {
    return resendBackupCodesEmail(TwoFactorEmailOtpContext.Regenerate)
  }

  async function acknowledgeSetup (): Promise<Result<undefined>> {
    loading.value = true
    errors.value = []

    const result = await gateway.acknowledgeSetup()

    result.ifSuccess(() => {
      model.value = {
        enabled: true,
        method: selectedMethod.value
      }
    }).ifError((error) => {
      errors.value = [error]
    })

    loading.value = false

    return result
  }

  async function abandonSetup (): Promise<Result<undefined>> {
    loading.value = true
    errors.value = []

    const result = await gateway.abandonSetup()

    result.ifSuccess(() => {
      model.value = defaultStatus()
      backupCodesRemaining.value = undefined
      backupCodes.value = undefined
    }).ifError((error) => {
      errors.value = [error]
    })

    loading.value = false

    return result
  }

  function resetSetupFlow (): void {
    setupStep.value = undefined
    setupChallengeId.value = undefined
    selectedMethod.value = undefined
    totpSetup.value = undefined
    backupCodes.value = undefined
    errors.value = []
    setupEmailResendBlocked.value = false
    verificationBlocked.value = false
  }

  function clearBackupCodesCache (): void {
    backupCodes.value = undefined
    backupCodesRemaining.value = undefined
  }

  function openSetupWizard (): void {
    resetSetupFlow()
    setupStep.value = TwoFactorSetupStep.Method
  }

  return {
    model,
    errors,
    loading,
    setupStep,
    setupChallengeId,
    selectedMethod,
    totpSetup,
    backupCodes,
    backupCodesRemaining,
    setupEmailResendBlocked,
    verificationBlocked,
    defaultStatus,
    fetchStatus,
    fetchBackupCodesRemaining,
    startTotpSetup,
    startEmailSetup,
    resendSetupEmail,
    verifySetup,
    confirmSetup,
    acknowledgeSetup,
    abandonSetup,
    disable,
    regenerateBackupCodes,
    revealBackupCodes,
    sendRevealBackupCodesEmail,
    resendRevealBackupCodesEmail,
    sendRegenerateBackupCodesEmail,
    resendRegenerateBackupCodesEmail,
    resetSetupFlow,
    clearBackupCodesCache,
    openSetupWizard
  }
})

export default useTwoFactorStore

if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useTwoFactorStore, (import.meta as any).hot))
}
